import type { WordsFrequencyData } from "@-/dev.improve.haniverse.data.words-frequency";
import { wordsFrequency } from "@-/dev.improve.haniverse.data.words-frequency";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { and, count, eq, isNotNull, lte } from "@-/com.npmjs.drizzle-orm";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import { useMemo } from "react";

/**
 * Which catalog words survive the corpus filter. The catalog is the SUBTLEX-WF-ranked `word` rows in
 * rank order, so a simplified cutoff is a prefix (`count`); a traditional (BIAU) cutoff is a set.
 */
export type WordsFrequencyCorpusSelection =
  | { count: number; kind: "prefix" }
  | { kind: "set"; words: ReadonlySet<string> };

/**
 * The catalog restricted to `selection`, empty buckets dropped. `uniqueGraphemes` is kept as-is: it
 * only feeds the batched pinyin read, for which a superset is harmless and recomputing is not free.
 */
export function filterWordsFrequencyData(
  data: WordsFrequencyData,
  selection: WordsFrequencyCorpusSelection,
): WordsFrequencyData {
  let remaining = selection.kind === "prefix" ? selection.count : 0;
  const buckets = data.buckets.flatMap((bucket) => {
    let words: string[];
    if (selection.kind === "prefix") {
      words = bucket.words.slice(0, Math.max(0, remaining));
      remaining -= words.length;
    } else {
      words = bucket.words.filter((word) => selection.words.has(word));
    }
    return isNonEmpty(words) ? [{ ...bucket, words }] : [];
  });
  return { buckets, uniqueGraphemes: data.uniqueGraphemes };
}

/**
 * The bundled words-frequency catalog, restricted to the user's word corpus: the bundled object
 * itself (no query, no allocation) when the corpus has no word cutoff, `undefined` while a cutoff's
 * selection loads. A failed selection query throws to the page's error boundary.
 */
export function useCorpusWordsFrequencyData(): WordsFrequencyData | undefined {
  const { hanziScript, wordMaxRank } = useMorphemeCorpusFilter();
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const selectionQuery = useQuery({
    // No cutoff: seeded, so the unfiltered catalog never suspends or queries.
    initialData: wordMaxRank === null ? null : undefined,
    queryFn: async (): Promise<WordsFrequencyCorpusSelection | null> => {
      if (wordMaxRank === null) {
        return null;
      }
      const db = staticDb(import.meta.location);
      if (hanziScript === "simplified") {
        // The catalog is exactly the ranked `word` rows, so the in-corpus words are its first
        // `count(min_rank <= max)` entries — one indexed count instead of shipping the words.
        const [row] = await db
          .select({ count: count() })
          .from(staticSchema.word)
          .where(
            and(isNotNull(staticSchema.word.minRank), lte(staticSchema.word.minRank, wordMaxRank)),
          );
        return { count: row?.count ?? 0, kind: "prefix" };
      }
      const rows = await db
        .select({ word: staticSchema.word.word })
        .from(staticSchema.wordBiau)
        .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordBiau.wordId))
        .where(lte(staticSchema.wordBiau.rank, wordMaxRank));
      return { kind: "set", words: new Set(rows.map((row) => row.word)) };
    },
    // The selection depends only on the word half of the corpus, so character-only setting changes
    // keep the cached selection.
    queryKey: ["CorpusWordsFrequencySelection", hanziScript, wordMaxRank, { staticDb }],
    staleTime: Number.POSITIVE_INFINITY,
  });
  if (selectionQuery.error !== null && selectionQuery.data === undefined) {
    throw selectionQuery.error;
  }
  const selection = selectionQuery.data;
  return useMemo(
    () =>
      selection === undefined
        ? undefined
        : selection === null
          ? wordsFrequency
          : filterWordsFrequencyData(wordsFrequency, selection),
    [selection],
  );
}
